// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// 088 (Revision 2): the domain walk. The pending pool stores proposed
// statements in the original grammar, per part of the process (one source
// of truth, never a second model). The walk reviews ONE part at a time
// and, within it, one GROUP at a time — the concepts an SME actually
// confirms, in a fixed order that keeps every group a clean set of
// statements whose acceptance never dangles:
//
//   1. who acts           — initiators (people / external systems) and
//                           their actions, each through its touchpoint
//   2. which area handles — bounded contexts, what each handles in answer
//      it                   to which action, and what it raises / shows
//   3. what happens next  — reactions, what they raise, what is shown
//
// Everything here is DERIVED from the lines with the real parser and
// rendered in plain language; nothing is persisted. The ghost preview
// mirrors the server's accept (closure + id remap in functions/
// _domainFlow.js) so what the canvas shows dimmed is exactly what Accept
// lands. Keep the two in step.

import { parseFlow } from './flowParser';
import type { Statement, TriggerExpr } from './flowModel';
import type { StepNode } from './stepFlow';

export type WalkGroupKind = 'who-acts' | 'which-area' | 'what-next';

export interface WalkRow {
  id: number;
  // Plain-language line for the row (never the notation).
  text: string;
  // Raw grammar tokens for edit-in-place, kept verbatim so a save round-trips.
  rawName: string;
  rawSys: string[];
  // The events this row raises/shows, as their own renameable chips —
  // each is a pending statement in its own right, so renames target it
  // directly (the owner must be able to change "Incident notified").
  raised: Array<{ id: number; name: string; rawName: string; shown: boolean }>;
}

export interface WalkConcept {
  // Plain-language name shown on the chip, and the raw actor token behind it.
  name: string;
  rawName: string;
  kind: 'human' | 'externalSystem' | 'context';
  rows: WalkRow[];
}

export interface WalkGroup {
  kind: WalkGroupKind;
  title: string;
  question: string;
  concepts: WalkConcept[];
  // Every statement the group accepts (the server adds each one's closure).
  statementIds: number[];
}

export const WALK_ORDER: Array<{ kind: WalkGroupKind; title: string }> = [
  { kind: 'who-acts', title: 'Who acts' },
  { kind: 'which-area', title: 'Which area handles it' },
  { kind: 'what-next', title: 'What happens next' },
];

// CamelCase / PascalCase names read as prose ("RecordConsumption" →
// "Record consumption"). Hyphens and underscores become spaces too.
export function humaniseName(name: string): string {
  const spaced = (name || '')
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

const lower = (text: string) => (text ? text.charAt(0).toLowerCase() + text.slice(1) : text);

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function triggerRefs(expr: TriggerExpr): number[] {
  if ('ref' in expr) return [expr.ref];
  return [...triggerRefs(expr.left), ...triggerRefs(expr.right)];
}

function raisedChips(statement: Statement, byId: Map<number, Statement>): WalkRow['raised'] {
  return statement.emits
    .map((eventId) => byId.get(eventId))
    .filter((event): event is Statement => Boolean(event))
    .map((event) => ({
      id: event.id,
      name: humaniseName(event.name),
      rawName: event.name,
      shown: event.tags?.ui === true || event.tags?.ui === 'true',
    }));
}

function conceptsFor(
  statements: Statement[],
  rowText: (statement: Statement) => string,
  byId: Map<number, Statement>,
): WalkConcept[] {
  const concepts: WalkConcept[] = [];
  for (const statement of statements) {
    const actor = statement.actor;
    if (!actor || actor.kind === 'spine') continue;
    let concept = concepts.find((candidate) => candidate.rawName === actor.name);
    if (!concept) {
      concept = { name: humaniseName(actor.name), rawName: actor.name, kind: actor.kind, rows: [] };
      concepts.push(concept);
    }
    concept.rows.push({
      id: statement.id,
      text: rowText(statement),
      rawName: statement.name,
      rawSys: statement.via?.sys ?? [],
      raised: raisedChips(statement, byId),
    });
  }
  return concepts;
}

// Derive the walk groups for one part's pending lines. `confirmedText` is
// the confirmed domain flow, used only to name things a pending line
// refers to that were already accepted (an action a command follows from).
export function deriveWalkGroups(lines: string[], confirmedText = ''): WalkGroup[] {
  const pending = parseFlow(lines.join('\n')).statements;
  const confirmed = confirmedText.trim() ? parseFlow(confirmedText).statements : [];
  const byId = new Map<number, Statement>();
  for (const statement of confirmed) byId.set(statement.id, statement);
  for (const statement of pending) byId.set(statement.id, statement);

  // Touchpoints ride beside the action as their own editable chips
  // (`rawSys`), so the action text is the action alone.
  const actionText = (statement: Statement) => humaniseName(statement.name);
  const handlingText = (statement: Statement) => {
    const pieces = [humaniseName(statement.name)];
    const trigger = statement.triggeredBy;
    if (trigger?.kind === 'from') {
      const action = byId.get(trigger.actionId);
      if (action) {
        const who = action.actor ? ` by ${humaniseName(action.actor.name)}` : '';
        pieces.push(`after "${humaniseName(action.name)}"${who}`);
      }
    } else if (trigger?.kind === 'on') {
      const names = triggerRefs(trigger.expr)
        .map((ref) => byId.get(ref))
        .filter((event): event is Statement => Boolean(event))
        .map((event) => humaniseName(event.name));
      if (names.length > 0) pieces.push(`on "${names.join('", "')}"`);
    }
    // Raised/shown events are NOT baked into the text — they render as
    // their own renameable chips (WalkRow.raised).
    return pieces.join(', ');
  };
  const reactionText = (statement: Statement) => {
    const pieces: string[] = [];
    if (statement.triggeredBy?.kind === 'on') {
      const names = triggerRefs(statement.triggeredBy.expr)
        .map((ref) => byId.get(ref))
        .filter((event): event is Statement => Boolean(event))
        .map((event) => humaniseName(event.name));
      if (names.length > 0) pieces.push(`Reacts to "${names.join('", "')}" with ${lower(humaniseName(statement.name))}`);
    }
    if (pieces.length === 0) pieces.push(humaniseName(statement.name));
    return pieces.join(', ');
  };

  const actions = pending.filter((statement) => statement.type === 'action');
  const handling = pending.filter((statement) => statement.type === 'command' || statement.type === 'query');
  const reactions = pending.filter((statement) => statement.type === 'reaction');

  const groups: WalkGroup[] = [];
  const whoActs = conceptsFor(actions, actionText, byId);
  if (whoActs.length > 0) {
    const names = whoActs.map((concept) => concept.name);
    groups.push({
      kind: 'who-acts',
      title: 'Who acts',
      question: `I found ${joinNames(names)} acting in this part, and these are the actions I think belong to ${names.length > 1 ? 'each' : 'them'}. Is that right?`,
      concepts: whoActs,
      statementIds: actions.map((statement) => statement.id),
    });
  }
  const whichArea = conceptsFor(handling, handlingText, byId);
  if (whichArea.length > 0) {
    const names = whichArea.map((concept) => concept.name);
    groups.push({
      kind: 'which-area',
      title: 'Which area handles it',
      question: names.length > 1
        ? `I have been calling the areas that handle this ${joinNames(names)}. Are those your words?`
        : `I have been calling the area that handles this ${names[0]}. Is that your word?`,
      concepts: whichArea,
      statementIds: handling.map((statement) => statement.id),
    });
  }
  const whatNext = conceptsFor(reactions, reactionText, byId);
  if (whatNext.length > 0) {
    groups.push({
      kind: 'what-next',
      title: 'What happens next',
      question: 'Here is what I think follows on from that. Does it read right?',
      concepts: whatNext,
      statementIds: reactions.map((statement) => statement.id),
    });
  }
  return groups;
}

// The ghost preview: exactly the lines Accept would land, with the ids
// Accept would give them — the server's closure and remap, mirrored.
export function planAcceptance(
  confirmedText: string,
  lines: string[],
  statementIds: number[],
): { ghostLines: string[]; ghostIds: number[] } {
  const leadingId = (line: string) => {
    const match = line.match(/^\s*\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : null;
  };
  const byId = new Map<number, string>();
  for (const line of lines) {
    const id = leadingId(line);
    if (id !== null) byId.set(id, line);
  }
  const closure = new Set<number>();
  const visit = (id: number) => {
    if (closure.has(id) || !byId.has(id)) return;
    closure.add(id);
    for (const match of byId.get(id)!.matchAll(/\[(\d+)\]/g)) {
      const ref = parseInt(match[1], 10);
      if (ref !== id) visit(ref);
    }
  };
  for (const id of statementIds) visit(id);
  const acceptedIds = [...byId.keys()].filter((id) => closure.has(id));

  let maxId = 0;
  for (const match of (confirmedText || '').matchAll(/^\s*\[(\d+)\]/gm)) {
    maxId = Math.max(maxId, parseInt(match[1], 10));
  }
  const remap = new Map(acceptedIds.map((id, index) => [id, maxId + 1 + index]));
  const ghostLines = acceptedIds.map((id) => byId.get(id)!.replace(/\[(\d+)\]/g, (token, ref) => {
    const mapped = remap.get(parseInt(ref, 10));
    return mapped === undefined ? token : `[${mapped}]`;
  }));
  return { ghostLines, ghostIds: acceptedIds.map((id) => remap.get(id)!) };
}

// A part is a top-level step; any step selects the part it belongs to.
export function partOfStep(steps: StepNode[], stepId: number): number | null {
  const contains = (node: StepNode): boolean =>
    node.id === stepId || node.steps.some(contains);
  const part = steps.find(contains);
  return part ? part.id : null;
}

// Every step id inside a part (the part itself included), for highlighting.
export function stepIdsOfPart(steps: StepNode[], partId: number): number[] {
  const part = steps.find((node) => node.id === partId);
  if (!part) return [];
  const ids: number[] = [];
  const walk = (node: StepNode) => { ids.push(node.id); node.steps.forEach(walk); };
  walk(part);
  return ids;
}
